import { Ban, CircleCheck, Edit, Plus, Search, Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { DataTable } from "../DataTable/DataTable";
import { useAuth } from "../../context/AuthContext";
import { InverntroyService } from "../../services/api/inventroy.service";
import toast from "react-hot-toast";
import AddLabTestForm from "../../shared/AddLabtest/AddNewTest";
import { LabService } from "../../services/api/LaborderService/lab.service";
import {
  LabtestListingData,
  LabTestListingDetail,
} from "../../services/api/LaborderService/types";

export function StockLabTestsView() {
  const [isLoading, setIsLoading] = useState(true);
  const [labTestsList, setLabTestsList] = useState<LabtestListingData[]>([]);
  const [selectedLabTest, setSelectedLabTest] = useState<number | null>(null);
  const [showAddLabTestModal, setShowAddLabTestModal] = useState(false);

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");

  // tab state
  const [activeTab, setActiveTab] = useState<
    "single" | "head" | "profile" | "healthPackage"
  >("single");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  // --- load lab tests listing ---
  const loadLabTestsListing = async (
    page: number,
    pageSize: number,
    name?: string,
  ) => {
    try {
      setIsLoading(true);
      const isHealthPackage = activeTab === "healthPackage"; // ✅ toggle based on tab
      let type = "single";
      if (activeTab === "head") {
        type = "panel";
      } else if (activeTab === "healthPackage") {
        type = "health_package";
      } else if (activeTab === "profile") {
        type = "profile";
      }
      const response = await LabService.getLabtestListing(
        page,
        pageSize,
        isHealthPackage,
        type,
        name,
      );

      if (response?.success) {
        console.log(response?.data?.labTestDetails, "labTestDetails");
        setLabTestsList(response?.data?.labTestDetails);
        setCurrentPage(response?.data?.currentPage);
        setTotalPages(response?.data?.totalPages);
      }
    } catch (error) {
      toast.error(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  // --- delete lab test ---
  const handleDeleteLabTest = async (labTestId: number) => {
    try {
      const deleteLabTestResponse =
        await InverntroyService.deleteLabTest(labTestId);
      if (deleteLabTestResponse.success) {
        toast.success(deleteLabTestResponse.message);
        loadLabTestsListing(currentPage, pageSize);
      } else {
        toast.error(deleteLabTestResponse.message);
      }
    } catch (error) {
      toast.error(`${error}`);
    }
  };

  const handleOnSuccess = async () => {
    await loadLabTestsListing(currentPage, pageSize);
    setShowAddLabTestModal(false);
  };

  const handleOnClose = async () => {
    await loadLabTestsListing(currentPage, pageSize);
    setShowAddLabTestModal(false);
    setSelectedLabTest(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const addLabTestButton = (
    <button
      onClick={() => setShowAddLabTestModal(true)}
      className="relative z-10 flex items-center justify-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap"
    >
      <Plus className="w-5 h-5 mr-2" />
      <span className="whitespace-nowrap">
        Add {activeTab === "healthPackage" ? "Health Package" : "Lab Test"}
      </span>
    </button>
  );

  const columns = [
    {
      key: "name",
      title: "Name",
      width: "w-[200px]",
      render: (item: LabtestListingData) => (
        <div className="flex items-center space-x-2">
          <div
            className="font-medium text-sm line-clamp-2 cursor-default"
            title={item?.name}
          >
            {item?.name}
          </div>
        </div>
      ),
    },
    {
      key: "testDescription",
      title: "Test Description",
      width: "w-[200px]",
      render: (item: LabtestListingData) => (
        <div
          className="text-sm font-medium line-clamp-2 cursor-default"
          title={item?.description}
        >
          {item?.description}
        </div>
      ),
    },
    {
      key: "price",
      title: "Price",
      width: "w-[200px]",
      render: (item: LabtestListingData) => (
        <div className="text-sm font-medium">{item?.offerPrice}</div>
      ),
    },
    {
      key: "reportTime",
      title: "Report Time",
      width: "w-[200px]",
      render: (item: LabtestListingData) => (
        <div className="text-sm font-medium">{item?.reportTime}</div>
      ),
    },
    ...(activeTab !== "single"
      ? [
          {
            key: "testsIncluded",
            title: "Tests Included",
            width: "w-[200px]",
            render: (item: LabtestListingData) => (
              <div className="text-sm font-medium">{item?.includedTests}</div>
            ),
          },
        ]
      : []),
    {
      key: "published",
      title: "Published",
      width: "w-[200px]",
      render: (item: LabtestListingData) => (
        <div>
          <div className="text-sm font-medium">
            {item.isPublished ? (
              <CircleCheck className="w-5 h-5 bg-[#199C4D] rounded-full text-white" />
            ) : (
              <Ban className="w-5 h-5 bg-[#c62a2a] rounded-full text-white" />
            )}
          </div>
        </div>
      ),
    },
    {
      key: "actions",
      title: "Actions",
      width: "w-[200px]",
      render: (item: LabtestListingData) => (
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setShowAddLabTestModal(true);
              setSelectedLabTest(item.id);
            }}
            className="text-yellow-500 hover:text-yellow-700"
          >
            <Edit className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDeleteLabTest(item.id)}
            className="text-red-500 hover:text-red-700"
          >
            <Trash className="w-5 h-5" />
          </button>
        </div>
      ),
    },
  ];

  const handleActiveTabChange = (
    tab: "head" | "single" | "healthPackage" | "profile",
  ) => {
    setActiveTab(tab);
    setSelectedLabTest(null);
    setShowAddLabTestModal(false);
  };

  useEffect(() => {
    loadLabTestsListing(currentPage, pageSize, searchText);
  }, [currentPage, pageSize, activeTab, searchText]); // ✅ added activeTab dependency

  return (
    <div className="relative">
      {/* ✅ Tabs Section */}
      <div className="flex items-center justify-between border-b mb-4 pb-2">
        {/* Tabs */}
        <div className="flex space-x-4">
          {[
            { key: "single", label: "Single Tests" },
            { key: "head", label: "Head Tests" },
            { key: "profile", label: "Profile Tests" },
            { key: "healthPackage", label: "Health Packages" },
          ].map((tab: { key: string; label: string }) => (
            <button
              key={tab.key}
              onClick={() => handleActiveTabChange(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                activeTab === tab.key
                  ? "border-emerald-600 text-emerald-600"
                  : "border-transparent text-gray-500 hover:text-emerald-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
      </div>

      
      <div className="relative top-0">
        <div className="flex justify-end relative top-10">
          <div>
            <Search className="relative top-[2.25rem] left-4 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchText}
              onChange={handleSearchChange}
              className="border rounded-lg px-8 py-2.5  text-sm w-64 text-center"
            />
          </div>
        </div>
        {showAddLabTestModal ? (
          <AddLabTestForm
            onSuccess={handleOnSuccess}
            onClose={handleOnClose}
            activeTab={activeTab}
            selectedLabTest={selectedLabTest}
          />
        ) : (
          <DataTable
            data={labTestsList}
            columns={columns}
            isLoading={isLoading}
            customFilters={isAdmin && addLabTestButton}
            pagination={{
              currentPage,
              setCurrentPage,
              setTotalPages,
              totalPages,
              pageSize,
              setPageSize,
            }}
          />
        )}
      </div>
    </div>
  );
}
